// src/components/Control/Operator/Settings.tsx
// Настройки — отдельно от операторского экрана, по разделам. Шапка со STOP и
// голосом остаётся видна. Сырые данные (JSON, топики) — только в «Диагностике».
import React, { useState } from 'react';
import { useEventsState, useRobot, useTelemetryState, useUi, useUiActions, useZenohInfo, useDriveState, useHasCam2, useHasChassis, SettingsSection } from '../robot/RobotContext';
import { EventSource, SOURCE_NAMES, TOAST_PREF, toastSources } from '../robot/events';
import { useTick } from '../../../hooks/useTick';
import { putJson } from '../../../hooks/useZenohSubscription';
import { loadPref, savePref, timeOf } from '../../../types/features';
import { ZenohSettings as ZenohSettingsType } from '../../../types';
import AutonomyPanel from '../ControlPanel/AutonomyPanel';
import CameraSettings from '../ControlPanel/CameraSettings';
import Cam2Obstacle from '../ControlPanel/Cam2Obstacle';
import PeoplePanel from '../ControlPanel/PeoplePanel';
import LidarViewer from '../ControlPanel/LidarViewer';
import MapsPanel from '../ControlPanel/MapsPanel';
import ZenohSettingsForm from '../ControlPanel/ZenohSettings';
import RobotInfo from '../ControlPanel/RobotInfo';
import SystemStatus from '../StatusPanel/SystemStatus';
import ZenohMonitor from '../StatusPanel/ZenohMonitor';
import VisionPanel from '../Features/VisionPanel';
import PowerPanel from '../ControlPanel/PowerPanel';
import MicTest from '../Features/MicTest';
import GeometryPanel from './Geometry';
import EnergyChart from './EnergyChart';
import { PanTiltSettings } from './PanTilt';
import { RecordingPlayer, RecordingSettings } from './Recordings';
import LaserPanel from './Laser';
import { SnapshotGallery, SnapshotSettings } from './Snapshots';
import TrainerPanel from './Trainer';
import SmartHomePanel from './SmartHome';
import VoicePanel from '../Features/VoicePanel';
import BluetoothPanel from '../Features/BluetoothPanel';
import { NannySettings } from '../Features/NannyPanel';
import { KeyboardHelp, SpeedControl } from './ControlPad';
import { useRobotStatus } from './status';
import { Icon } from './ui';
import { CameraView } from './Camera';
import { STALE, fresh } from '../robot/telemetry';

const SECTIONS: [SettingsSection, string, string][] = [
  ['autonomy', 'Автономия', 'объезд препятствий, подъезд, приветствие'],
  ['cameras', 'Камеры', 'передача, FPS, поворотная камера, препятствия по камере 2'],
  ['people', 'Люди и распознавание', 'знакомые, лица, поза, QR/ArUco, предметы'],
  ['lidar', 'LiDAR', 'вид лидара, источник препятствий'],
  ['navigation', 'Управление и навигация', 'клавиатура, скорость, стоп по лидару, езда по клику'],
  ['geometry', 'Геометрия робота', 'габариты, колёса, лидар, камеры, бампер'],
  ['recording', 'Запись с камеры', 'постоянно или по движению, хранение, просмотр записей'],
  ['snapshots', 'Снимки с камер', 'куда сохранять, наборы для обучения, серия, выгрузка ZIP'],
  ['laser', 'Лазер и рулетка', 'навести луч на точку стены, измерить расстояние'],
  ['trainer', 'Фитнес-тренер', 'упражнение по камере: повторы, подсказки голосом, подходы'],
  ['home', 'Умный дом и Грач', 'голосовой дворецкий, светодиодные ленты WLED'],
  ['maps', 'Карты', 'сохранённые карты, автосохранение, карта предметов'],
  ['nanny', 'Няня', 'пороги звука, ключевые слова, запись'],
  ['voice', 'Голос и звук', 'распознавание, динамик, громкость, тест микрофона'],
  ['bluetooth', 'Bluetooth', 'колонки и наушники робота'],
  ['power', 'Питание и сон', 'кто на связи, сон, перезапуск и выключение'],
  ['notifications', 'Уведомления', 'всплывающие, звук, браузер, история событий'],
  ['zenoh', 'Zenoh', 'подключение'],
  ['diagnostics', 'Диагностика', 'график питания, сырые данные, топики, прошивки'],
];

// Разделы, которым нужно шасси (колёса, лидар, карта): у Pi без шасси их нет
const CHASSIS_SECTIONS: SettingsSection[] = ['autonomy', 'lidar', 'navigation', 'geometry', 'laser', 'maps'];

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="feature-card"><h3><span>{title}</span></h3>{children}</div>
);

const LidarSection: React.FC = () => {
  const { session, robotId } = useRobot();
  const sources = useTelemetryState(t => t.status?.settings?.autonomy?.sources ?? null);
  return (
    <>
      <Card title="Лидар">
        <div className="op-lidar-box">
          <LidarViewer zenohSession={session} scanKey={`robot/${robotId}/scan`} />
        </div>
      </Card>
      <Card title="Препятствия для автономии">
        {sources ? (
          <label className="op-check">
            <input type="checkbox" checked={sources.lidar}
                   onChange={e => session?.put(`robot/${robotId}/config`, JSON.stringify({ autonomy: { sources: { lidar: e.target.checked } } })).catch(() => {})} />
            Учитывать лидар при объезде препятствий
          </label>
        ) : <p className="hint">Нет настроек от робота.</p>}
        <p className="hint">Отдельных настроек лидара у робота нет; остальные источники — в разделе «Автономия».</p>
      </Card>
    </>
  );
};

// Камера 1 рядом с «Запомнить этого человека» / «Запомнить лицо»: видно, кого робот видит сейчас
const PeopleCamera: React.FC = () => {
  useTick(1000);
  const { people, personAt } = useTelemetryState(t => ({ people: t.people, personAt: t.personAt }));
  const inFrame = fresh(personAt, STALE.person) ? people : [];
  const names = inFrame.map(p => p.known?.name).filter(Boolean);
  return (
    <div className="feature-card op-people-cam">
      <h3><span>Камера 1 сейчас</span></h3>
      <CameraView cam="0" className="op-people-cam-view" />
      <p className="op-people-cam-note" role="status">
        {inFrame.length
          ? `В кадре: ${inFrame.length} ${inFrame.length === 1 ? 'человек' : 'чел.'}${names.length ? ` (${names.join(', ')})` : ''}. Запомнится ближайший (самая большая рамка).`
          : 'В кадре никого нет — встаньте перед роботом, чтобы он вас запомнил.'}
      </p>
    </div>
  );
};

const LidarStop: React.FC = () => {
  const { session, robotId } = useRobot();
  const on = useTelemetryState(t => t.status?.settings?.lidar_stop);
  const block = useTelemetryState(t => t.status?.drive_block ?? null);
  if (on === undefined) return <p className="hint">Стоп по лидару: робот не поддерживает (старая версия ПО на Pi).</p>;
  return (
    <>
      <label className="op-check">
        <input type="checkbox" checked={on}
               onChange={e => putJson(session, `robot/${robotId}/config`, { lidar_stop: e.target.checked })} />
        Не ехать вперёд в препятствие по лидару (кнопки, клавиатура, джойстик)
      </label>
      <p className="hint">Робот останавливается, если лидар видит что-то в коридоре шириной с корпус ближе 5 см + полсекунды
        хода; поворачивать можно. Габарит — в «Геометрия робота». Низкое, ниже луча лидара, он не видит.
        {block ? ` Сейчас «вперёд» не выполняется: препятствие (${block === 'lidar' ? 'лидар' : 'камера 2'}).` : ''}</p>
    </>
  );
};

const NavigationSection: React.FC = () => (
  <Card title="Ручное управление">
    <SpeedControl />
    <LidarStop />
    <KeyboardHelp />
    <p className="hint">Кнопки и джойстик: «Управление» над картой (на телефоне — кнопка «Управление» или свайп вверх от нижнего края).
      Держите — едет, отпустили — стоп. Езда к точке: «Ехать по клику» на панели карты.</p>
  </Card>
);

const MapsSection: React.FC = () => {
  const { session, robotId, notify } = useRobot();
  const slam = useTelemetryState(t => (t.status as any)?.slam ?? null);
  const maps = useTelemetryState(t => t.status?.settings?.maps ?? null);
  return (
    <>
      {session ? (
        <MapsPanel zenohSession={session} robotId={robotId} slamStatus={slam} mapsSettings={maps} />
      ) : <p className="hint">Нет связи с сервером.</p>}
      <VisionPanel zenohSession={session} robotId={robotId} onNotify={notify} part="map" />
    </>
  );
};

const NotificationsSection: React.FC = () => {
  const { notify } = useRobot();
  const { openSettings } = useUiActions();
  const [sources, setSources] = useState(toastSources);
  const [browser, setBrowser] = useState(() => loadPref('nanny.notify', false));
  const [nannyBeep, setNannyBeep] = useState(() => loadPref('nanny.beep', true));
  const [visionBeep, setVisionBeep] = useState(() => loadPref('vision.beep', true));
  const [filter, setFilter] = useState<'all' | 'alerts' | EventSource>('all');
  const events = useEventsState(e => e.events);

  const toggleBrowser = async (on: boolean) => {
    if (on && 'Notification' in window && Notification.permission !== 'granted') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        notify('Браузер не разрешил уведомления', 'error');
        on = false;
      }
    }
    setBrowser(on);
    savePref('nanny.notify', on);
  };
  const setSource = (s: EventSource, on: boolean) => {
    const next = { ...sources, [s]: on };
    setSources(next);
    savePref(TOAST_PREF, next);
  };
  const shown = events.filter(e => filter === 'all' || (filter === 'alerts' ? e.level === 'alert' || e.level === 'error' : e.source === filter));

  return (
    <>
      <Card title="Всплывающие уведомления">
        <p className="hint">От каких источников показывать уведомления поверх экрана (в истории события есть всегда):</p>
        <div className="checks">
          {(Object.keys(SOURCE_NAMES) as EventSource[]).map(s => (
            <label key={s}><input type="checkbox" checked={sources[s]} onChange={e => setSource(s, e.target.checked)} /> {SOURCE_NAMES[s]}</label>
          ))}
        </div>
      </Card>
      <Card title="Звук и браузер">
        <div className="checks">
          <label><input type="checkbox" checked={browser} onChange={e => toggleBrowser(e.target.checked)} /> Уведомления браузера (няня и тревоги зрения)</label>
          <label><input type="checkbox" checked={nannyBeep} onChange={e => { setNannyBeep(e.target.checked); savePref('nanny.beep', e.target.checked); }} /> Звуковой сигнал няни</label>
          <label><input type="checkbox" checked={visionBeep} onChange={e => { setVisionBeep(e.target.checked); savePref('vision.beep', e.target.checked); }} /> Звуковой сигнал тревоги зрения</label>
        </div>
        <p className="hint">Частота оповещений задаётся на роботе:
          <button type="button" className="btn-icon link" onClick={() => openSettings('nanny')}>пауза между оповещениями няни</button>,
          <button type="button" className="btn-icon link" onClick={() => openSettings('people')}>«не чаще раза в N с» для зрения</button>.</p>
      </Card>
      <Card title="История событий">
        <div className="op-chips" role="radiogroup" aria-label="Фильтр событий">
          {(['all', 'alerts', ...(Object.keys(SOURCE_NAMES) as EventSource[])] as const).map(f => (
            <button key={f} type="button" role="radio" aria-checked={filter === f} className={`op-chip-btn ${filter === f ? 'on' : ''}`} onClick={() => setFilter(f)}>
              {f === 'all' ? 'Все' : f === 'alerts' ? 'Тревоги и ошибки' : SOURCE_NAMES[f]}
            </button>
          ))}
        </div>
        {shown.length === 0 ? <p className="hint">Событий нет.</p> : (
          <ul className="event-list">
            {shown.slice(0, 150).map(ev => (
              <li key={ev.id} className={ev.level === 'alert' || ev.level === 'error' ? 'alert' : ev.level === 'warn' ? 'warn' : ev.level === 'ok' ? 'ok' : ''}>
                <span className="time">{timeOf(ev.at)}</span>
                <span className="text"><b>{SOURCE_NAMES[ev.source]}:</b> {ev.text}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
};

const ZenohSection: React.FC = () => {
  const { robotId, zenoh } = useRobot();
  const state = useZenohInfo(z => z.state);
  const [form, setForm] = useState<ZenohSettingsType>({
    host: window.location.host, port: '', cmdVelTopic: `robot/${robotId}/cmd_vel`, statusTopic: `robot/${robotId}/status`,
    streamTopic: `robot/${robotId}/cams/0`, scanTopic: `robot/${robotId}/scan`,
  });
  return (
    <Card title="Zenoh">
      <p>Состояние: <b>{{ connected: 'подключено', connecting: 'подключение…', reconnecting: 'переподключение…', idle: 'отключено' }[state]}</b>
        <span className="hint"> · браузер подключается к роутеру через /zenoh-ws этого сайта</span></p>
      <div className="row">
        {state === 'idle' ? (
          <button type="button" className="op-btn primary" onClick={zenoh.connect}>🔄 Подключить Zenoh</button>
        ) : (
          <button type="button" className="op-btn" onClick={zenoh.disconnect}>🔄 Отключить Zenoh</button>
        )}
      </div>
      <ZenohSettingsForm settings={form} onChange={e => setForm(f => ({ ...f, [e.target.name]: e.target.value }))} onSave={() => {}} />
    </Card>
  );
};

const Diagnostics: React.FC = () => {
  useTick(1000);
  const { robotId } = useRobot();
  const s = useRobotStatus();
  const raw = useTelemetryState(t => t.raw);
  const messages = useZenohInfo(z => z.messages);
  const lastCommand = useDriveState(d => d.lastCommand);
  const sent = useDriveState(d => d.lastSent);
  const vision = useTelemetryState(t => t.vision);
  const cam2cfg = useTelemetryState(t => (t.status?.settings?.cam2 as any)?.detector ?? null);
  const events = useEventsState(e => e.events);
  const st = s.status;
  return (
    <>
      <EnergyChart />
      <Card title="Робот">
        <RobotInfo robotData={{
          id: robotId,
          status: st?.status === 'online' ? 'online' : 'offline',
          zenohStatus: s.zstate === 'connected' ? 'connected' : 'disconnected',
          lastContact: s.statusAt ? new Date(s.statusAt).toLocaleTimeString() : '-',
          battery: s.battery != null ? `${s.battery}%` : '-',
          speed: s.speed != null ? `${s.speed.toFixed(2)} м/с` : '-',
        }} />
        <SystemStatus robotData={{ lastCommand, currentTime: new Date().toLocaleTimeString(), systemStatus: s.conn.text }} />
        <div className="info-grid op-diag-grid">
          <div className="info-item"><span className="label">Zenoh (WebSocket /zenoh-ws):</span><span>{s.zstate}</span></div>
          <div className="info-item"><span className="label">Связь Pi ↔ контроллер:</span><span>{st?.link ?? '—'}</span></div>
          <div className="info-item"><span className="label">Прошивка контроллера:</span><span>{st?.firmware ?? '—'}</span></div>
          <div className="info-item"><span className="label">Прошивка камеры 2:</span><span>{st?.cam2?.firmware ?? '—'}</span></div>
          <div className="info-item"><span className="label">Детектор препятствий (кам. 2):</span><span>{cam2cfg ?? '—'}</span></div>
          <div className="info-item"><span className="label">Зрение:</span><span>{vision ? Object.entries(vision.backends).map(([k, v]) => `${k}: ${v || '—'}`).join(', ') : '—'}</span></div>
          <div className="info-item"><span className="label">Напряжение:</span><span>{s.voltage != null ? `${s.voltage} В` : '—'}</span></div>
        </div>
        {sent && <p className={`op-sent ${sent.ok ? '' : 'bad'}`}>Последняя команда движения: {sent.text} · {timeOf(sent.at)}</p>}
      </Card>
      <Card title="Последние сообщения робота (JSON)">
        {Object.keys(raw).length === 0 ? <p className="hint">Сообщений пока нет.</p> : Object.entries(raw).sort().map(([key, v]) => (
          <details key={key} className="op-raw">
            <summary><code>robot/{robotId}/{key}</code> · {timeOf(v.at)}</summary>
            <pre>{v.text}</pre>
          </details>
        ))}
      </Card>
      <Card title="Zenoh: подключение и статус">
        <div className="zenoh-monitor">
          <ZenohMonitor messages={messages.slice(-60).reverse().map(m => `[${m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : 'N/A'}] [${m.key}]: ${m.value || 'No value'}`)} />
        </div>
      </Card>
      <Card title="Технические сообщения">
        <ul className="event-list">
          {events.filter(e => e.source === 'system' || e.level === 'error').slice(0, 60).map(ev => (
            <li key={ev.id} className={ev.level === 'error' ? 'alert' : ''}><span className="time">{timeOf(ev.at)}</span><span className="text">{ev.text}</span></li>
          ))}
        </ul>
      </Card>
    </>
  );
};

const SectionBody: React.FC<{ section: SettingsSection }> = ({ section }) => {
  const { session, robotId, notify } = useRobot();
  const show = useUi(u => u.show);
  const { setShow } = useUiActions();
  const props = { zenohSession: session, robotId, onNotify: notify };
  const cam2 = useHasCam2();
  switch (section) {
    case 'autonomy': return <AutonomyPanel zenohSession={session} robotId={robotId} />;
    case 'cameras': return (
      <>
        <CameraSettings zenohSession={session} robotId={robotId} show={show} onShowChange={setShow} />
        <PanTiltSettings />
        {cam2 && <Card title="Препятствия по камере 2 сейчас"><Cam2Obstacle /></Card>}
      </>
    );
    case 'people': return (
      <div className="op-people-layout">
        <PeopleCamera />
        <div className="op-people-panels">
          <PeoplePanel zenohSession={session} robotId={robotId} />
          <VisionPanel {...props} part="recognition" />
        </div>
      </div>
    );
    case 'lidar': return <LidarSection />;
    case 'navigation': return <NavigationSection />;
    case 'maps': return <MapsSection />;
    case 'nanny': return <NannySettings />;
    case 'voice': return <><VoicePanel {...props} /><MicTest /></>;
    case 'bluetooth': return <BluetoothPanel {...props} />;
    case 'geometry': return <GeometryPanel />;
    case 'recording': return <><RecordingSettings /><RecordingPlayer /></>;
    case 'snapshots': return <><SnapshotSettings /><SnapshotGallery /></>;
    case 'laser': return <LaserPanel />;
    case 'trainer': return <TrainerPanel />;
    case 'home': return <SmartHomePanel />;
    case 'power': return <PowerPanel />;
    case 'notifications': return <NotificationsSection />;
    case 'zenoh': return <ZenohSection />;
    case 'diagnostics': return <Diagnostics />;
  }
};

const Settings: React.FC<{ variant: 'desktop' | 'mobile' }> = ({ variant }) => {
  const chassis = useHasChassis();
  const sections = chassis ? SECTIONS : SECTIONS.filter(([id]) => !CHASSIS_SECTIONS.includes(id));
  const asked = useUi(u => u.section);
  const section = asked && sections.some(([id]) => id === asked) ? asked : null;
  const { openSettings, closeSettings } = useUiActions();
  const current = section ?? (variant === 'desktop' ? sections[0][0] : null);
  const title = SECTIONS.find(s => s[0] === current)?.[1];

  const nav = (
    <nav className="op-settings-nav" aria-label="Разделы настроек">
      {sections.map(([id, name, hint]) => (
        <button key={id} type="button" className={current === id ? 'on' : ''} aria-current={current === id ? 'page' : undefined}
                onClick={() => openSettings(id)}>
          <span>{name}</span>
          {variant === 'mobile' && <small>{hint}</small>}
        </button>
      ))}
    </nav>
  );

  if (variant === 'mobile') {
    return (
      <div className="op-settings mobile">
        {current ? (
          <>
            <div className="op-settings-title">
              <button type="button" className="op-icon-btn" aria-label="К разделам" onClick={() => openSettings(null)}><Icon name="back" /></button>
              <h2>{title}</h2>
            </div>
            <div className="op-settings-body"><SectionBody section={current} /></div>
          </>
        ) : (
          <>
            <div className="op-settings-title"><h2>Настройки</h2></div>
            {nav}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="op-settings desktop">
      <aside>
        <div className="op-settings-title">
          <h2>Настройки</h2>
          <button type="button" className="op-icon-btn" aria-label="Закрыть настройки" onClick={closeSettings}><Icon name="close" /></button>
        </div>
        {nav}
      </aside>
      <div className="op-settings-body" key={current ?? ''}>
        <h2 className="op-section-title">{title}</h2>
        {current && <SectionBody section={current} />}
      </div>
    </div>
  );
};

export default Settings;
